import { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Clock, User, ArrowRight, BookOpen, Video, FileText } from 'lucide-react'

export interface ContentCardProps {
  title: string
  description: string
  category: string
  readTime?: string
  author?: string
  type?: 'article' | 'video' | 'guide' | 'case-study'
  image?: string
  tags?: string[]
  onClick?: () => void
  className?: string
  children?: ReactNode
}

export function ContentCard({
  title,
  description,
  category,
  readTime,
  author,
  type = 'article',
  image,
  tags = [],
  onClick,
  className,
  children,
}: ContentCardProps) {
  const getTypeIcon = () => {
    switch (type) {
      case 'video':
        return <Video className="h-4 w-4" />
      case 'guide':
        return <BookOpen className="h-4 w-4" />
      case 'case-study':
        return <FileText className="h-4 w-4" />
      default:
        return <FileText className="h-4 w-4" />
    }
  }

  const getTypeLabel = () => {
    switch (type) {
      case 'video':
        return 'Video'
      case 'guide':
        return 'Guide'
      case 'case-study':
        return 'Case Study'
      default:
        return 'Article'
    }
  }

  return (
    <article
      onClick={onClick}
      className={cn(
        'content-card group relative overflow-hidden',
        'border border-border hover:border-primary/30',
        'transition-all duration-200',
        className
      )}
    >
      {/* Image Section */}
      {image && (
        <div className="aspect-video w-full overflow-hidden bg-muted">
          <img
            src={image}
            alt={title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>
      )}

      {/* Content Section */}
      <div className="p-6">
        {/* Type Badge */}
        <div className="flex items-center gap-2 mb-3">
          <span className="badge badge-primary text-xs">
            {getTypeIcon()}
            <span className="ml-1">{getTypeLabel()}</span>
          </span>
          <span className="badge badge-default text-xs">{category}</span>
        </div>

        {/* Title */}
        <h3 className="text-xl font-semibold text-card-foreground mb-2 group-hover:text-primary transition-colors">
          {title}
        </h3>

        {/* Description */}
        <p className="text-muted-foreground mb-4 line-clamp-2">
          {description}
        </p>

        {/* Meta Information */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
          {readTime && (
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>{readTime}</span>
            </div>
          )}
          {author && (
            <div className="flex items-center gap-1">
              <User className="h-4 w-4" />
              <span>{author}</span>
            </div>
          )}
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {tags.map((tag) => (
              <span
                key={tag}
                className="text-xs text-muted-foreground hover:text-primary transition-colors cursor-pointer"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Children or CTA */}
        {children ? (
          children
        ) : (
          <div className="flex items-center text-primary font-medium text-sm group-hover:gap-2 transition-all">
            <span>Read More</span>
            <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
          </div>
        )}
      </div>
    </article>
  )
}

// Featured Content Card - Larger variant for homepage
export interface FeaturedContentCardProps extends Omit<ContentCardProps, 'className'> {
  featured?: boolean
}

export function FeaturedContentCard({
  featured = true,
  ...props
}: FeaturedContentCardProps) {
  return (
    <ContentCard
      {...props}
      className={cn(
        'border-primary/20 shadow-glow',
        featured && 'ring-1 ring-primary/30'
      )}
    />
  )
}

// Content Grid Component
export interface ContentGridProps {
  children: ReactNode
  columns?: 1 | 2 | 3 | 4
  className?: string
}

export function ContentGrid({ children, columns = 3, className }: ContentGridProps) {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  }

  return (
    <div className={cn('grid gap-6', gridCols[columns], className)}>
      {children}
    </div>
  )
}
